import { PersonAutocomplete } from './PersonAutocomplete';

export interface PersonRef {
  id: string;
  firstName: string;
  lastName: string | null;
}

interface PersonMultiSelectProps {
  /** Aktuell zugeordnete Personen. */
  value: PersonRef[];
  onChange: (persons: PersonRef[]) => void;
  placeholder?: string;
}

function fullName(p: PersonRef): string {
  return `${p.firstName} ${p.lastName ?? ''}`.trim();
}

/**
 * Mehrfach-Personenauswahl: Chips für die zugeordneten Personen + Suchfeld zum
 * Hinzufügen (nutzt PersonAutocomplete). Bereits gewählte Personen werden aus
 * der Suche ausgeschlossen.
 */
export function PersonMultiSelect({ value, onChange, placeholder = 'Person hinzufügen…' }: PersonMultiSelectProps) {
  const add = (p: PersonRef) => {
    if (value.some((v) => v.id === p.id)) return;
    onChange([...value, { id: p.id, firstName: p.firstName, lastName: p.lastName }]);
  };
  const remove = (id: string) => onChange(value.filter((v) => v.id !== id));

  return (
    <div className="space-y-2">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((p) => (
            <span
              key={p.id}
              className="inline-flex items-center gap-1 rounded-full bg-blue-50 py-0.5 pl-2.5 pr-1 text-xs font-medium text-blue-700"
            >
              {fullName(p)}
              <button
                type="button"
                onClick={() => remove(p.id)}
                className="rounded-full p-0.5 text-blue-400 hover:bg-blue-100 hover:text-blue-700"
                aria-label={`${fullName(p)} entfernen`}
              >
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Immer im Suchmodus (value=null), Auswahl fügt eine Person hinzu. */}
      <PersonAutocomplete
        value={null}
        onChange={() => {}}
        onSelectPerson={(person) =>
          add({ id: person.id, firstName: person.firstName, lastName: person.lastName })
        }
        excludeIds={value.map((v) => v.id)}
        placeholder={placeholder}
      />
    </div>
  );
}
